import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ContactRole, type Contact } from '@/data/types';
import type { I18nKey } from '@/lib/i18n';
import { useT } from '@/lib/i18n';

export const CONTACT_ROLE_LABEL: Record<ContactRole, I18nKey> = {
  [ContactRole.JANITOR]: 'contact.role.janitor',
  [ContactRole.JANITOR_2]: 'contact.role.janitor2',
  [ContactRole.MANAGEMENT]: 'contact.role.management',
  [ContactRole.SAFETY_OFFICER]: 'contact.role.safetyOfficer',
  [ContactRole.CUSTOM]: 'contact.role.custom',
};

/** Display label for a contact's role — the free text for custom roles. */
export function contactRoleLabel(
  c: Contact,
  t: (k: I18nKey) => string
): string {
  if (c.role === ContactRole.CUSTOM && c.customRole?.trim())
    return c.customRole.trim();
  return t(CONTACT_ROLE_LABEL[c.role] ?? 'contact.role.janitor');
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial?: Contact;
  onSave: (c: Contact) => void;
}

export function ContactDialog({ open, onOpenChange, initial, onSave }: Props) {
  const t = useT();
  const [c, setC] = useState<Contact>(initial ?? { role: ContactRole.JANITOR });

  const set = <K extends keyof Contact>(k: K, v: Contact[K]) =>
    setC(prev => ({ ...prev, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {initial ? t('contact.editTitle') : t('contact.addTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label htmlFor="contact-role">{t('contact.role')}</Label>
            <select
              id="contact-role"
              className="flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={c.role}
              onChange={e => set('role', e.target.value as ContactRole)}
            >
              {Object.values(ContactRole).map(r => (
                <option key={r} value={r}>
                  {t(CONTACT_ROLE_LABEL[r])}
                </option>
              ))}
            </select>
          </div>
          {c.role === ContactRole.CUSTOM && (
            <div>
              <Label htmlFor="contact-custom-role">
                {t('contact.customRole')}
              </Label>
              <Input
                id="contact-custom-role"
                value={c.customRole ?? ''}
                onChange={e => set('customRole', e.target.value)}
              />
            </div>
          )}
          <div>
            <Label htmlFor="contact-name">{t('contact.name')}</Label>
            <Input
              id="contact-name"
              value={c.name ?? ''}
              onChange={e => set('name', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="contact-phone">{t('contact.phone')}</Label>
            <Input
              id="contact-phone"
              value={c.phone ?? ''}
              onChange={e => set('phone', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="contact-email">{t('address.email')}</Label>
            <Input
              id="contact-email"
              value={c.email ?? ''}
              onChange={e => set('email', e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              onSave(c);
              onOpenChange(false);
            }}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
